import { Add as AddIcon } from '@mui/icons-material';
import { Box, Button, Chip, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { confirmToast, errorToast, successToast } from '@/common/toast';
import { Container, DataGrid } from '@/components/common';
import { DateCell } from '@/components/pages/components';
import { LANGUAGE_OPTIONS } from '@/constants/trainings';
import { useDeleteQuestion, useQuestions } from '@/hooks/aiReview.hook';
import { LIST_VIEW_SESSION_KEYS } from '@/util/listViewSessionKeys';
import { useListViewSessionPersistence } from '@/util/useListViewSessionPersistence';

const PROJECTS_DEFAULT_PAGINATION = { page: 0, pageSize: 10 };
const PROJECTS_DEFAULT_FILTERS = { languageId: null };

/**
 * 프로젝트 개발훈련 관리 - 목록 페이지
 * languageId를 URL params로 관리 (북마크/공유 시 상태 유지)
 */
const ProjectPage = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const urlLangOnceRef = useRef(false);

    const {
        paginationModel,
        setPaginationModel,
        filters,
        setFilters,
    } = useListViewSessionPersistence(
        LIST_VIEW_SESSION_KEYS.projects,
        PROJECTS_DEFAULT_PAGINATION,
        PROJECTS_DEFAULT_FILTERS
    );

    const languageId = filters.languageId ?? null;

    // 최초 1회: URL의 languageId가 있으면 세션보다 우선
    useEffect(() => {
        if (urlLangOnceRef.current) return;
        const lang = searchParams.get('languageId');
        if (lang) {
            urlLangOnceRef.current = true;
            const parsed = parseInt(lang, 10);
            if (!Number.isNaN(parsed)) {
                setFilters((f) => ({ ...f, languageId: parsed }));
            }
        }
    }, [searchParams, setFilters]);

    // 언어 변경 핸들러 (URL 동기화)
    const handleLanguageChange = useCallback((event) => {
        const selectedLanguageId = event.target.value === '' ? null : parseInt(event.target.value, 10);
        setFilters((f) => ({ ...f, languageId: selectedLanguageId }));
        setPaginationModel((p) => ({ ...p, page: 0 }));
        if (selectedLanguageId != null) {
            setSearchParams({ languageId: String(selectedLanguageId) }, { replace: true });
        } else {
            setSearchParams({}, { replace: true });
        }
    }, [setSearchParams, setFilters, setPaginationModel]);

    const { data, isLoading } = useQuestions(languageId);
    const deleteQuestionMutation = useDeleteQuestion();

    const rowData = Array.isArray(data) ? data : (data?.list ?? data?.data ?? []);

    const handleRowClick = useCallback((params) => {
        const id = params.row.id ?? params.row.questionId;
        navigate(`/education/projects/detail/${id}`);
    }, [navigate, languageId]);

    const handleDelete = useCallback((params, e) => {
        e?.stopPropagation();
        const id = params.row.id ?? params.row.questionId;
        const title = params.row.title ?? params.row.question ?? '-';
        confirmToast(`"${title}" 프로젝트를 삭제하시겠습니까?`, async () => {
            try {
                await deleteQuestionMutation.mutateAsync(id);
                successToast('프로젝트가 삭제되었습니다.');
            } catch (err) {
                errorToast(`프로젝트 삭제에 실패했습니다. - ${err.response?.data?.msg}`);
            }
        });
    }, [deleteQuestionMutation]);

    const columns = useMemo(() => [
        {
            field: 'id',
            headerName: 'ID',
            flex: 0.3,
            minWidth: 80,
            sortable: false,
        },
        {
            field: 'title',
            headerName: '제목',
            flex: 1,
            minWidth: 200,
            sortable: false,
        },
        {
            field: 'createdAt',
            headerName: '등록일',
            flex: 0.3,
            minWidth: 120,
            sortable: false,
            renderCell: (params) => (
                <DateCell row={params.row} dateField="createdAt" />
            ),
        },
        {
            field: 'isApproved',
            headerName: '승인 여부',
            flex: 0.4,
            minWidth: 140,
            sortable: false,
            renderCell: (params) => {
                const statusInfo = params.row.isApproved === true
                    ? { color: 'success', label: '승인' }
                    : { color: 'error', label: '반려' };
                return (
                    <Chip
                        label={statusInfo.label}
                        size="small"
                        color={statusInfo.color}
                        variant="outlined"
                    />
                );
            },
        },
        {
            field: 'actions',
            headerName: '작업',
            flex: 0.5,
            minWidth: 100,
            sortable: false,
            renderCell: (params) => (
                <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={(e) => handleDelete(params, e)}
                >
                    삭제
                </Button>
            ),
        },
    ], [handleDelete]);

    return (
        <Container title="프로젝트 개발훈련 관리">
            {/* 언어 선택 영역 */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3} gap={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControl size='small' sx={{ width: 200 }} required>
                        <InputLabel>언어 선택</InputLabel>
                        <Select
                            value={languageId || ''}
                            label="언어 선택"
                            onChange={handleLanguageChange}
                        >
                            {LANGUAGE_OPTIONS.map((lang) => (
                                <MenuItem key={lang.languageId} value={lang.languageId}>
                                    {lang.languageName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {!languageId && (
                        <Typography variant="body2" color="text.secondary">
                            언어를 선택하면 문제 목록을 조회할 수 있습니다.
                        </Typography>
                    )}
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        navigate(`/education/projects/new`);
                    }}
                    sx={{ height: 40 }}
                >
                    프로젝트 등록
                </Button>
            </Stack>

            {!languageId ? (
                <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 400,
                    border: '1px dashed #ccc',
                    borderRadius: 1,
                    bgcolor: '#fafafa',
                }}
                >
                    <Typography variant="h6" color="text.secondary">
                        언어를 선택하면 문제 목록을 확인할 수 있습니다.
                    </Typography>
                </Box>
                    ) : (
                <DataGrid
                    rows={rowData}
                    columns={columns}
                    loading={isLoading}
                    height={'auto'}
                    paginationMode="client"
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    getRowId={(row, index) => row.id ?? row.questionId ?? `row-${index}`}
                    onRowClick={handleRowClick}
                />
            )}
        </Container>
    );
};

export default ProjectPage;
